import React, { useEffect, useRef, useState } from "react";
import SearchBar from "../SearchBar";
import FilterSheet from "./FilterSheet";
import { Badge } from "@/components/ui/badge";
import { X, ListFilter, Brush } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import InfiniteScrollTags from "./InfiniteScrollTags";
import LocationFilterModal from "./LocationFilterModal";

interface SearchSectionProps {
  selectedDisciplines: string[];
  selectedLocations: string[];
  onDisciplineToggle: (disciplineId: string) => void;
  onLocationToggle: (locationId: string) => void;
  onResetFilters: () => void;
  disciplines: Array<{
    id: string;
    label: string;
  }>;
  locations: Array<{
    id: string;
    label: string;
  }>;
}

const SearchSection: React.FC<SearchSectionProps> = ({
  selectedDisciplines,
  selectedLocations,
  onDisciplineToggle,
  onLocationToggle,
  onResetFilters,
  disciplines,
  locations,
}) => {
  const isMobile = useIsMobile();
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);

  // Function to find the label for a discipline by id
  const getDisciplineLabel = (id: string) => {
    const discipline = disciplines.find((d) => d.id === id);
    return discipline ? discipline.label : id;
  };

  // Function to find the label for a location by id
  const getLocationLabel = (id: string) => {
    const location = locations.find((l) => l.id === id);
    return location ? location.label : id;
  };

  // Get currently selected location label
  const currentLocationLabel =
    selectedLocations.length === 1
      ? getLocationLabel(selectedLocations[0])
      : selectedLocations.length > 1
      ? `${selectedLocations.length} locations`
      : "All Locations";

  // Count selected items
  const selectedDisciplineCount = selectedDisciplines.length;
  const selectedLocationCount = selectedLocations.length;

  // Open the location filter modal
  const handleOpenLocationModal = () => {
    setIsLocationModalOpen(true);
  };

  return (
    <div className="mb-6">
      <div className="border-b border-gray-200 my-4" />
      {/* Filter UI for all devices */}
      <div className="space-y-4">
        {/* Combined row with fixed location button and scrollable discipline tags */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            {selectedDisciplineCount > 0 && (
              <span className="text-xs text-accent font-medium">
                {selectedDisciplineCount} selected
              </span>
            )}
          </div>

          <div className="relative flex flex-nowrap items-center gap-2 overflow-x-auto whitespace-nowrap pb-2 -mx-1 px-1 scrollbar-hide">
            {/* Auto-scrolling discipline tags container with hover pause */}
            <InfiniteScrollTags
              items={disciplines}
              selectedItems={selectedDisciplines}
              onItemToggle={onDisciplineToggle}
              direction="right"
            />

            {/* Fixed Location Button that opens the modal - now on the right */}
            <div className="sticky right-0 z-10 min-w-[90px] flex-shrink-0 flex gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenLocationModal}
                className="w-full border-zinc-200 hover:border-accent text-sm flex items-center"
              >
                <ListFilter className="h-4 w-4" />
                <span>
                  {selectedLocations.length > 0
                    ? `${selectedLocations.length} locations`
                    : "Countries"}
                </span>
              </Button>
            </div>
          </div>
        </div>

        {/* Selected Filters Display */}
        {(selectedDisciplines.length > 0 || selectedLocations.length > 0) && (
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <span className="text-xs text-muted-foreground">
              Selected filters:
            </span>

            {selectedDisciplines.map((id) => (
              <Badge
                key={`selected-discipline-${id}`}
                variant="default"
                className="flex items-center gap-1 pl-3 pr-2 py-1 bg-accent text-white cursor-pointer group"
                onClick={() => onDisciplineToggle(id)}
              >
                <span className="text-xs">{getDisciplineLabel(id)}</span>
                <X
                  size={14}
                  className="group-hover:text-white/80 transition-colors"
                />
              </Badge>
            ))}

            {selectedLocations.map((id) => (
              <Badge
                key={`selected-location-${id}`}
                variant="secondary"
                className="flex items-center gap-1 pl-3 pr-2 py-1 cursor-pointer group"
                onClick={() => onLocationToggle(id)}
              >
                <span className="text-xs">{getLocationLabel(id)}</span>
                <X
                  size={14}
                  className="group-hover:text-accent transition-colors"
                />
              </Badge>
            ))}

            <button
              className="text-xs text-muted-foreground underline hover:text-accent"
              onClick={onResetFilters}
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Location Filter Modal */}
      <LocationFilterModal
        isOpen={isLocationModalOpen}
        onOpenChange={setIsLocationModalOpen}
        selectedLocations={selectedLocations}
        onLocationToggle={onLocationToggle}
        onResetFilters={onResetFilters}
        locations={locations}
      />
    </div>
  );
};

export default SearchSection;
